function normalize(value) {
  return String(value || "").trim().toLowerCase();
}

function toNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function getWorkerRoles(worker) {
  return [
    worker?.primaryRole,
    worker?.workerType,
    worker?.roleType,
    worker?.licenseType,
    ...(Array.isArray(worker?._validTypes)
      ? worker._validTypes
      : []),
    ...(Array.isArray(worker?.approvedPositions)
      ? worker.approvedPositions
      : []),
  ]
    .map(normalize)
    .filter(Boolean);
}

function getDistanceMiles(worker) {
  return toNumber(
    worker?.distanceMiles ??
      worker?.distanceFromFacilityMiles ??
      worker?._distanceMiles ??
      worker?.commuteDistanceMiles,
    null
  );
}

function getAttendance(worker) {
  return toNumber(
    worker?.attendancePercent ??
      worker?.attendanceRate ??
      worker?.attendance ??
      worker?.metrics?.attendance,
    100
  );
}

function getReliability(worker) {
  return toNumber(
    worker?.reliabilityScore ??
      worker?.reliability ??
      worker?.metrics?.reliability,
    100
  );
}

function getExperienceYears(worker) {
  return toNumber(
    worker?.experienceYears ??
      worker?.yearsExperience ??
      worker?.yearsOfExperience,
    0
  );
}

function hasTransportation(worker) {
  return (
    worker?.hasTransportation === true ||
    worker?.reliableTransportation === true ||
    normalize(worker?.transportation) === "reliable" ||
    normalize(worker?.transportationStatus) === "verified"
  );
}

export function buildSmartMatch(worker, shift = {}) {
  if (!worker) {
    return {
      score: 0,
      reasons: [],
      breakdown: {},
    };
  }

  let score = 0;
  const reasons = [];
  const breakdown = {};

  const shiftRole = normalize(
    shift?.role || shift?.requiredPosition
  );

  const workerRoles = getWorkerRoles(worker);
  const roleMatch =
    !shiftRole || workerRoles.includes(shiftRole);

  breakdown.role = roleMatch ? 25 : 0;
  score += breakdown.role;

  if (roleMatch) {
    reasons.push("Role and credential match");
  }

  const attendance = Math.max(
    0,
    Math.min(100, getAttendance(worker))
  );

  breakdown.attendance = Math.round(
    (attendance / 100) * 20
  );

  score += breakdown.attendance;

  if (attendance >= 95) {
    reasons.push("Excellent attendance");
  } else if (attendance >= 85) {
    reasons.push("Good attendance");
  }

  const reliability = Math.max(
    0,
    Math.min(100, getReliability(worker))
  );

  breakdown.reliability = Math.round(
    (reliability / 100) * 20
  );

  score += breakdown.reliability;

  if (reliability >= 95) {
    reasons.push("Excellent reliability");
  } else if (reliability >= 85) {
    reasons.push("Good reliability");
  }

  const distanceMiles = getDistanceMiles(worker);

  if (distanceMiles === null) {
    breakdown.distance = 5;
  } else if (distanceMiles <= 5) {
    breakdown.distance = 15;
    reasons.push("Very close to facility");
  } else if (distanceMiles <= 15) {
    breakdown.distance = 12;
    reasons.push("Close to facility");
  } else if (distanceMiles <= 30) {
    breakdown.distance = 8;
  } else if (distanceMiles <= 50) {
    breakdown.distance = 4;
  } else {
    breakdown.distance = 0;
  }

  score += breakdown.distance;

  if (hasTransportation(worker)) {
    breakdown.transportation = 10;
    score += breakdown.transportation;
    reasons.push("Reliable transportation");
  } else {
    breakdown.transportation = 0;
  }

  const experienceYears = getExperienceYears(worker);

  if (experienceYears >= 5) {
    breakdown.experience = 10;
    reasons.push("Strong experience");
  } else if (experienceYears >= 2) {
    breakdown.experience = 7;
    reasons.push("Relevant experience");
  } else if (experienceYears > 0) {
    breakdown.experience = 4;
  } else {
    breakdown.experience = 0;
  }

  score += breakdown.experience;

  return {
    score: Math.max(0, Math.min(100, Math.round(score))),
    reasons,
    breakdown: {
      ...breakdown,
      attendancePercent: attendance,
      reliabilityPercent: reliability,
      distanceMiles,
      experienceYears,
    },
  };
}

export function computeWorkerMatchScore(worker, shift = {}) {
  return buildSmartMatch(worker, shift).score;
}